import type { FunctionReturnType } from "convex/server"
import type { api } from "@/lib/api"
import { WORK_STATES, stateOf, type WorkState } from "../../../convex/lib/workState"

export type Work = FunctionReturnType<typeof api.work.list>["work"][number]
export type JournalLine = FunctionReturnType<typeof api.work.journal>["page"][number]

// Where a worktree stands is decided in convex/lib/workState.ts, so that the
// page and an agent reading Work through MCP say the same.
export { WORK_STATES, isStale, stateOf, type WorkState } from "../../../convex/lib/workState"

/** Folded until asked for: edits on main are rarely a pull request someone forgot. */
export const FOLDED_BY_DEFAULT: readonly WorkState[] = ["defaultBranch"]

export function groupWork(work: Work[]): { state: WorkState; rows: Work[] }[] {
  return WORK_STATES.map((state) => ({
    state,
    rows: work.filter((row) => stateOf(row) === state),
  })).filter((group) => group.rows.length > 0)
}

/** What the project filter keeps: one project's rows, or those in none. */
export const NO_PROJECT = "none"
export function inProject(work: Pick<Work, "projectId">, filter: string | null): boolean {
  if (filter === null) return true
  return filter === NO_PROJECT ? work.projectId === null : work.projectId === filter
}

/** A machine that reports every quarter of an hour and has not for this long has stopped. */
const SILENT_AFTER_MS = 60 * 60_000
export function isSilent(lastSyncAt: number, now: number): boolean {
  return now - lastSyncAt >= SILENT_AFTER_MS
}

/** The last two parts of a path: enough to tell worktrees of one repository apart. */
export function shortPath(path: string): string {
  const parts = path.split("/").filter(Boolean)
  return parts.length <= 2 ? path : `…/${parts.slice(-2).join("/")}`
}

/** What a row says its repository is when it is in no project: the name, without the owner. */
export function repoName(repo: string | null): string | null {
  return repo ? (repo.split("/")[1] ?? repo) : null
}

/**
 * Where GitHub offers to open a pull request for a branch. Null unless the
 * branch is there to be compared: pushed, and not the base itself.
 */
export function compareUrl(work: Pick<Work, "repo" | "branch" | "base" | "pushed">): string | null {
  if (!work.repo || !work.branch || !work.base || !work.pushed || work.branch === work.base) return null
  const part = (ref: string) => ref.split("/").map(encodeURIComponent).join("/")
  return `https://github.com/${work.repo}/compare/${part(work.base)}...${part(work.branch)}?expand=1`
}

/** A path as a shell takes it, whatever is in it. `~` is left for the shell to expand. */
export function shellPath(path: string): string {
  const quote = (text: string) => `'${text.replace(/'/g, `'\\''`)}'`
  return path.startsWith("~/") ? `~/${quote(path.slice(2))}` : quote(path)
}

/** The journal by day, in the reader's own time zone, keeping its order. */
export function groupByDay<Line extends { at: number }>(lines: Line[]): { day: number; lines: Line[] }[] {
  const days: { day: number; lines: Line[] }[] = []
  for (const line of lines) {
    const date = new Date(line.at)
    const day = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
    const last = days.at(-1)
    if (last?.day === day) last.lines.push(line)
    else days.push({ day, lines: [line] })
  }
  return days
}

export type WorktreePanel = NonNullable<FunctionReturnType<typeof api.work.worktree>>
export type WorkSummary = FunctionReturnType<typeof api.work.summary>
export type WorkFile = NonNullable<NonNullable<WorktreePanel["detail"]>["files"]>[number]

/** What the journal can be kept to: one kind of line. */
export const JOURNAL_KINDS = ["started", "committed", "edited", "pushed", "removed"] as const
export type JournalKind = (typeof JOURNAL_KINDS)[number]

const MINUTE_MS = 60_000

/**
 * A time counted from reports, in whole hours and minutes: { hours, minutes }.
 * Rounded to the quarter hour the reports are counted in, and never shown as
 * nothing when something was counted.
 */
export function durationParts(ms: number): { hours: number; minutes: number } {
  if (ms <= 0) return { hours: 0, minutes: 0 }
  const quarters = Math.max(1, Math.round(ms / (15 * MINUTE_MS)))
  return { hours: Math.floor(quarters / 4), minutes: (quarters % 4) * 15 }
}

/** A branch's page on GitHub, once it is there. */
export function branchUrl(work: Pick<Work, "repo" | "branch" | "pushed">): string | null {
  if (!work.repo || !work.branch || !work.pushed) return null
  return `https://github.com/${work.repo}/tree/${work.branch.split("/").map(encodeURIComponent).join("/")}`
}

/** A commit's page on GitHub. Only a commit that is there: one not pushed is a 404. */
export function commitUrl(repo: string | null, sha: string): string | null {
  return repo ? `https://github.com/${repo}/commit/${sha}` : null
}

/** A file's directory and name, to draw the name stronger: ["src/components/", "row.tsx"]. */
export function splitPath(path: string): [string, string] {
  const slash = path.lastIndexOf("/", path.endsWith("/") ? path.length - 2 : path.length)
  return slash === -1 ? ["", path] : [path.slice(0, slash + 1), path.slice(slash + 1)]
}

/**
 * The commits of a branch that are on GitHub. The reporter lists them newest
 * first; the ones not pushed are the newest `unpushed`, or all of them on a
 * branch that never was.
 */
export function isPushedCommit(work: Pick<Work, "pushed" | "unpushed">, index: number): boolean {
  return work.pushed && index >= work.unpushed
}

/** The longest day of a summary, for the height of its bars. Never zero. */
export function longestDay(days: { activeMs: number }[]): number {
  return Math.max(MINUTE_MS, ...days.map((day) => day.activeMs))
}

// ── Actions ─────────────────────────────────────────────────────────────────

export type WorkAction = FunctionReturnType<typeof api.workActions.recent>[number]
export type WorkActionKind = WorkAction["kind"]

/** As convex/lib/limits.ts has it: a machine that asked within this takes actions. */
const LISTENING_MS = 10 * 60_000
/** As convex/lib/limits.ts has it: worktrees one "clean up all" asks for, at most. */
export const CLEANUP_BATCH = 50

/** Whether a machine has asked for actions lately, and so would do one asked of it. */
export function isListening(actionsSeenAt: number | null, now: number): boolean {
  return actionsSeenAt !== null && now - actionsSeenAt < LISTENING_MS
}

/** Put out of the way by its member, and not woken yet. */
export function isHidden(work: Pick<Work, "hidden">, now: number): boolean {
  return work.hidden !== null && (work.hidden.until === null || work.hidden.until > now)
}

type Actionable = Pick<
  Work,
  "mine" | "branch" | "base" | "repo" | "ahead" | "pushed" | "unpushed" | "changedFiles" | "untrackedFiles" | "pullRequest"
>

const onBranch = (work: Actionable) => work.branch !== null && work.branch !== work.base

/** Merged, with nothing left uncommitted: what can be removed, branch and all. */
export function canCleanUp(work: Actionable): boolean {
  return (
    work.mine &&
    onBranch(work) &&
    work.pullRequest?.state === "merged" &&
    work.changedFiles + work.untrackedFiles === 0
  )
}

/** Commits of its own that GitHub does not have. */
export function canPush(work: Actionable): boolean {
  return work.mine && onBranch(work) && work.repo !== null && work.ahead > 0 && (!work.pushed || work.unpushed > 0)
}

export function canFetch(work: Actionable): boolean {
  return work.mine && work.repo !== null
}

/** The action of each worktree that its machine has not finished: the newest. */
export function pendingByWork(actions: WorkAction[]): Map<string, WorkAction> {
  const pending = new Map<string, WorkAction>()
  for (const action of actions) {
    if (action.workId === null || pending.has(action.workId)) continue
    if (action.status === "queued" || action.status === "sent") pending.set(action.workId, action)
  }
  return pending
}

/** The command that does what the action does, for doing it by hand. */
export function commandOf(kind: Exclude<WorkActionKind, "sync">, work: Pick<Work, "path" | "branch">): string {
  const path = shellPath(work.path)
  const branch = shellPath(work.branch ?? "")
  if (kind === "cleanup") return `git worktree remove ${path}`
  if (kind === "push") return `git -C ${path} push -u origin ${branch}`
  return `git -C ${path} fetch origin`
}
